/**
 * Shared chart primitives + "trending" charts for the quarter board.
 * Palette per design guidelines: navy won, gold deciding, brick lost;
 * earthy team colours. MiniBars is the 8-quarter sparkline (line, area
 * fill, dots on current + same-quarter-last-year) used
 * inside stat tiles and the team panel.
 */
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { InfoTip } from "./methodology";

export const TEAM_HEX = {
  UK: "#2a9d8f",
  International: "#e9c46a",
  PSE: "#f4a261",
  PMS: "#e76f51",
  MediLAB: "#6d597a",
  Other: "#a8a29e",
};

export const OUTCOME_HEX = { won: "#1B4F8A", deciding: "#d4a373", lost: "#9b2226" };

export const tooltipStyle = {
  fontSize: 11,
  fontFamily: "'JetBrains Mono', monospace",
  borderRadius: 10,
  border: "1px solid #e7e5e4",
  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
};

/* ------------------------------------------------------- mini bars */

export function MiniBars({ trend, format, light = false, w = 150, h = 36, fluid = false }) {
  const pts = trend || [];
  const idx = pts.map((t, i) => ({ ...t, i })).filter((t) => t.value != null);
  if (idx.length < 2) return null;
  const vals = idx.map((t) => t.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const n = pts.length;
  const x = (i) => (i / (n - 1)) * (w - 8) + 4;
  const y = (v) =>
    max === min ? h / 2 : h - 5 - ((v - min) / (max - min)) * (h - 10);
  const fmt = format || ((v) => Math.round(v).toLocaleString("en-GB"));
  const line = idx
    .map((t, k) => `${k === 0 ? "M" : "L"}${x(t.i).toFixed(1)},${y(t.value).toFixed(1)}`)
    .join(" ");
  const area = `${line} L${x(idx[idx.length - 1].i).toFixed(1)},${h} L${x(idx[0].i).toFixed(1)},${h} Z`;
  const cur = idx[idx.length - 1];
  const yoy = idx.find((t) => t.i === n - 5);
  const stroke = light ? "rgba(255,255,255,0.65)" : "#94a3b8";
  const fill = light ? "rgba(255,255,255,0.10)" : "rgba(27,79,138,0.07)";
  return (
    <div
      className={fluid ? "mt-2 w-full" : "mt-2"}
      style={fluid ? undefined : { width: w }}
      data-testid="qkpi-minibars"
      aria-hidden="true"
    >
      <svg {...(fluid ? { viewBox: `0 0 ${w} ${h}`, width: "100%" } : { width: w, height: h })}>
        <path d={area} fill={fill} />
        <path d={line} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        {yoy && yoy.i !== cur.i && (
          <circle
            cx={x(yoy.i)}
            cy={y(yoy.value)}
            r="2.8"
            fill={light ? "#152b45" : "#fff"}
            stroke={light ? "rgba(255,255,255,0.8)" : "#a8a29e"}
            strokeWidth="1.5"
          >
            <title>{`${yoy.label} (same quarter last year): ${fmt(yoy.value)}`}</title>
          </circle>
        )}
        <circle cx={x(cur.i)} cy={y(cur.value)} r="3.2" fill={light ? "#7fd1c7" : "#1B4F8A"}>
          <title>{`${cur.label}: ${fmt(cur.value)}`}</title>
        </circle>
        {idx.map((t) => (
          <circle key={t.i} cx={x(t.i)} cy={y(t.value)} r="7" fill="transparent">
            <title>{`${t.label}: ${fmt(t.value)}`}</title>
          </circle>
        ))}
      </svg>
      <div
        className={`flex justify-between text-[8px] font-mono uppercase tracking-wide ${
          light ? "text-white/40" : "text-stone-400"
        }`}
      >
        <span>{pts[0]?.label}</span>
        <span>{cur.label}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- donuts */

function SubmittedTrend({ trend }) {
  const rows = (trend || []).filter((t) => t.n != null);
  if (rows.length < 2) return null;
  const lastLabel = rows[rows.length - 1].label;
  return (
    <div
      className="rounded-2xl border border-stone-200 bg-white shadow-sm p-6 q-card"
      data-testid="qchart-trend"
    >
      <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
        Opportunities quoted · last 8 quarters
        <InfoTip id="chart_trend" />
      </p>
      <div className="h-44 mt-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 14, right: 4, left: -26, bottom: 0 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 9, fontFamily: "monospace", fill: "#a8a29e" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 9, fontFamily: "monospace", fill: "#a8a29e" }}
              axisLine={false}
              tickLine={false}
            />
            <ReTooltip contentStyle={tooltipStyle} formatter={(v) => [v, "opportunities"]} cursor={{ fill: "#f5f5f4" }} />
            <Bar dataKey="n" radius={[6, 6, 0, 0]} isAnimationActive={true}>
              {rows.map((r) => (
                <Cell key={r.label} fill={r.label === lastLabel ? "#1B4F8A" : "#d6d3d1"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-stone-400 mt-1">This quarter highlighted in navy.</p>
    </div>
  );
}

export default function QuarterCharts({ charts, trend }) {
  if (!(trend || []).length) return null;
  return (
    <div className="grid grid-cols-1 gap-4" data-testid="quarter-charts">
      <SubmittedTrend trend={trend} />
    </div>
  );
}
